import { useEffect, useState } from 'react';
import { BeakerIcon, ExclamationTriangleIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { geneticsApi, type PairPredictionResult, type EggPredictionSnapshot } from '../../api/genetics';

interface ClutchGeneticsPanelProps {
  clutchId: string;
  eggCount?: number;
}

export default function ClutchGeneticsPanel({ clutchId, eggCount }: ClutchGeneticsPanelProps) {
  const [prediction, setPrediction] = useState<PairPredictionResult | null>(null);
  const [clutchEggs, setClutchEggs] = useState<Array<{ eggId: string; eggIndex: number; status: string }>>([]);
  const [snapshots, setSnapshots] = useState<EggPredictionSnapshot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [result, hatchSnapshots] = await Promise.all([
        geneticsApi.predictClutch(clutchId),
        geneticsApi.getClutchSnapshots(clutchId),
      ]);
      setPrediction(result);
      setClutchEggs(result.eggs ?? []);
      setSnapshots(hatchSnapshots ?? []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load clutch prediction');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clutchId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clutchId]);

  const slots = eggCount ?? clutchEggs.length;

  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-indigo-900 flex items-center gap-2">
          <BeakerIcon className="h-5 w-5" />
          Clutch Offspring Expectations
        </h4>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="text-xs text-indigo-700 hover:text-indigo-900 underline disabled:opacity-50"
        >
          {loading ? 'Calculating…' : 'Refresh'}
        </button>
      </div>

      {error && <p className="text-sm text-red-600 mb-2">{error}</p>}

      {slots > 0 && (
        <p className="text-xs text-slate-600 mb-3">
          Each of the {slots} egg{slots === 1 ? '' : 's'} in this clutch shares the same parental genetics
          probabilities below (siblings are independent draws from the same distribution).
        </p>
      )}

      {prediction?.warnings?.length ? (
        <div className="mb-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex gap-1">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{prediction.warnings[0]}</span>
        </div>
      ) : null}

      {prediction?.combinedPhenotypes?.length ? (
        <div className="space-y-1 mb-3">
          <p className="text-xs font-medium text-slate-700">Most likely combined phenotypes</p>
          {prediction.combinedPhenotypes.slice(0, 6).map((item) => (
            <div key={item.phenotype} className="text-xs flex justify-between gap-2 bg-white rounded px-2 py-1 border border-indigo-100">
              <span className="text-slate-800 truncate">{item.phenotype}</span>
              <span className="font-medium text-indigo-700 flex-shrink-0">{(item.probability * 100).toFixed(1)}%</span>
            </div>
          ))}
        </div>
      ) : !loading && !error ? (
        <p className="text-sm text-slate-600">Record parent genotypes to enable clutch predictions.</p>
      ) : null}

      {clutchEggs.length > 0 && (
        <div className="mt-2 pt-2 border-t border-indigo-100">
          <p className="text-xs font-medium text-slate-600 mb-1">Eggs in clutch</p>
          <div className="flex flex-wrap gap-1">
            {clutchEggs.map((egg) => {
              const snapshot = snapshots.find((s) => s.eggId === egg.eggId);
              const hasOutcome = snapshot?.matched !== null && snapshot?.matched !== undefined;
              return (
                <span
                  key={egg.eggId}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-white border text-slate-700 ${
                    hasOutcome
                      ? snapshot.matched
                        ? 'border-green-300'
                        : 'border-amber-300'
                      : snapshot
                        ? 'border-violet-200'
                        : 'border-slate-200'
                  }`}
                  title={
                    snapshot?.comparisonMessage ||
                    (snapshot ? 'Hatch prediction recorded' : 'No hatch snapshot yet')
                  }
                >
                  #{egg.eggIndex} · {egg.status.replace(/_/g, ' ').toLowerCase()}
                  {hasOutcome &&
                    (snapshot.matched ? (
                      <CheckCircleIcon className="h-3.5 w-3.5 text-green-600" />
                    ) : (
                      <XCircleIcon className="h-3.5 w-3.5 text-amber-600" />
                    ))}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
